import Dashboard from "./layouts/dashboard";
// @mui icons
import Icon from "@mui/material/Icon";
import Incidents from "./layouts/incidents";
import Anomalies from "./layouts/anomalies";
import AnomalyGuides from "./layouts/anomaly-guides";
import Customers from "./layouts/customers";
import SourceSystems from "./layouts/source-systems";
import IntentRules from "./layouts/intent-rules";
import MailFilters from "./layouts/mail-filters";
import MailSettings from "./layouts/mail-settings";
import Callback from "./layouts/callback";
import LogoutUI from "./layouts/logout";
import LoginUI from "./layouts/login";
import IncidentMainDetail from "./layouts/incident-detail";
import Subscription from "./layouts/subscription";
import Plans from "./layouts/plans";
import SubscriptionAdmin from "./layouts/subscription-admin";
import Profiles from "./layouts/profiles";

export const MENU_GROUPS = {
  detection: {
    name: "Detection",
    nameKey: "routes.groups.detection",
    icon: <Icon fontSize="small">sensors</Icon>,
  },
  email: {
    name: "Email",
    nameKey: "routes.groups.email",
    icon: <Icon fontSize="small">mail</Icon>,
  },
  subscriptions: {
    name: "Subscriptions",
    nameKey: "routes.groups.subscriptions",
    icon: <Icon fontSize="small">payments</Icon>,
  },
} as const;

export type MenuGroupKey = keyof typeof MENU_GROUPS;

const routes = [
  {
    whiteListed: true,
    menu: false,
    type: "collapse",
    name: "Default",
    key: "default",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/",
    component: <LoginUI />,
  },
  {
    menu: true,
    type: "collapse",
    name: "Dashboard",
    nameKey: "routes.dashboard",
    key: "dashboard",
    allowedRoles: ["admin", "viewer", "user", "reporter"],
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    menu: true,
    type: "collapse",
    key: "incidents",
    allowedRoles: ["admin", "viewer", "user", "reporter"],
    route: "/incidents",
    name: "Incidents",
    nameKey: "routes.incidents",
    icon: <Icon fontSize="small">inbox</Icon>,
    component: <Incidents />,
  },
  {
    menu: false,
    type: "collapse",
    key: "incident-detail",
    allowedRoles: ["admin", "viewer", "user", "reporter"],
    route: "/incidents/:id",
    name: "Incident Detail",
    nameKey: "routes.incidentDetail",
    icon: <Icon fontSize="small">visibility</Icon>,
    component: <IncidentMainDetail />,
  },
  {
    menu: true,
    type: "collapse",
    key: "anomalies",
    allowedRoles: ["admin", "user"],
    route: "/anomalies",
    name: "Anomalies",
    nameKey: "routes.anomalies",
    parent: "detection",
    icon: <Icon fontSize="small">radar</Icon>,
    component: <Anomalies />,
  },
  {
    menu: true,
    type: "collapse",
    key: "anomaly-guides",
    allowedRoles: ["admin", "user"],
    route: "/anomaly-guides",
    name: "Anomaly Guides",
    nameKey: "routes.anomalyGuides",
    parent: "detection",
    icon: <Icon fontSize="small">menu_book</Icon>,
    component: <AnomalyGuides />,
  },
  {
    menu: true,
    type: "collapse",
    key: "customers",
    allowedRoles: ["admin"],
    route: "/customers",
    name: "Customers",
    nameKey: "routes.customers",
    icon: <Icon fontSize="small">people</Icon>,
    component: <Customers />,
  },
  {
    menu: false,
    type: "collapse",
    key: "source-systems",
    allowedRoles: ["admin", "viewer", "user"],
    route: "/source-systems",
    name: "Source Systems",
    nameKey: "routes.sourceSystems",
    icon: <Icon fontSize="small">settings_input_component</Icon>,
    component: <SourceSystems />,
  },
  {
    menu: false,
    type: "collapse",
    key: "intent-rules",
    allowedRoles: ["admin"],
    route: "/intent-rules",
    name: "Intent Rules",
    nameKey: "routes.intentRules",
    icon: <Icon fontSize="small">psychology</Icon>,
    component: <IntentRules />,
  },
  {
    menu: true,
    type: "collapse",
    key: "mail-settings",
    allowedRoles: ["admin"],
    route: "/mail-settings",
    name: "Email Settings",
    nameKey: "routes.mailSettings",
    parent: "email",
    icon: <Icon fontSize="small">settings</Icon>,
    component: <MailSettings />,
  },
  {
    menu: true,
    type: "collapse",
    key: "mail-filters",
    allowedRoles: ["admin"],
    route: "/mail-filters",
    name: "Email Filters",
    nameKey: "routes.mailFilters",
    parent: "email",
    icon: <Icon fontSize="small">filter_alt</Icon>,
    component: <MailFilters />,
  },
  {
    menu: true,
    type: "collapse",
    key: "plans",
    allowedRoles: ["admin"],
    route: "/plans",
    name: "Plans",
    nameKey: "routes.plans",
    parent: "subscriptions",
    icon: <Icon fontSize="small">inventory_2</Icon>,
    component: <Plans />,
  },
  {
    menu: true,
    type: "collapse",
    key: "profiles",
    tenantUserProfileOnly: true,
    allowedRoles: ["user"],
    route: "/profiles",
    name: "Profile",
    nameKey: "routes.profiles",
    icon: <Icon fontSize="small">account_box</Icon>,
    component: <Profiles />,
  },
  {
    menu: true,
    type: "collapse",
    key: "subscription",
    allowedRoles: ["admin", "user", "reporter"],
    route: "/subscription",
    name: "My Subscription",
    nameKey: "routes.subscription",
    parent: "subscriptions",
    icon: <Icon fontSize="small">workspace_premium</Icon>,
    component: <Subscription />,
  },
  {
    menu: true,
    type: "collapse",
    key: "subscription-admin",
    allowedRoles: ["admin"],
    route: "/subscription-admin",
    name: "Manage",
    nameKey: "routes.subscriptionAdmin",
    parent: "subscriptions",
    icon: <Icon fontSize="small">manage_accounts</Icon>,
    component: <SubscriptionAdmin />,
  },
  {
    menu: true,
    type: "collapse",
    key: "connect-ai",
    name: "Connect AI",
    nameKey: "routes.connectAi",
    icon: <Icon fontSize="small">smart_toy</Icon>,
    href: "https://connect.socmanage.com",
  },
  {
    menu: true,
    type: "collapse",
    key: "ai-training",
    name: "AI Training",
    nameKey: "routes.aiTraining",
    icon: <Icon fontSize="small">model_training</Icon>,
    href: "https://ai.socmanage.com",
  },
  {
    menu: false,
    whiteListed: false,
    type: "collapse",
    key: "logout",
    route: "/logout",
    name: "Logout",
    icon: <Icon fontSize="small">power_settings_new</Icon>,
    component: <LogoutUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Login",
    key: "login",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/login",
    component: <LoginUI />,
  },
  {
    whiteListed: true,
    menu: false,
    type: "collapse",
    name: "callback",
    key: "callback",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/callback",
    component: <Callback />,
  },
];

export default routes;
